import { Injectable, inject } from '@angular/core';

import { mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { DeviceService } from '@era/osupdates-sdk';
import { safeMap } from '@era/stores';
import {
  AllowedOsUpdatesResolverActions,
  CurrentDeviceViewActions,
  OsUpdatesApiActions,
} from './allowed-os-updates.actions';

@Injectable()
export class AllowedOsUpdatesEffects {
  private readonly actions = inject(Actions);
  private readonly deviceService = inject(DeviceService);

  /**
   * The (re)load of the allowed os updates has been requested.
   * Effect: we load the state from the api.
   */
  AllowedOsUpdatesRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(AllowedOsUpdatesResolverActions.allowedUpdatesRequested),
      mergeMap(({ deviceId }) =>
        safeMap(
          this.deviceService.getAllowedOsUpdates({ udid: deviceId }),
          (appleDevice) =>
            OsUpdatesApiActions.deviceAllowedUpdatesLoaded({
              updates:
                appleDevice.allowedAndAvailableUpdates?.map((update) => ({
                  ...update,
                  deviceId: deviceId,
                })) ?? [],
            }),
          (error) => OsUpdatesApiActions.deviceAllowedUpdatesError({ error })
        )
      )
    );
  });

  /**
   * The OS update of a device has been requested.
   * Effect: we call the API to update the device to the latest allowed OS version.
   */
  updateDeviceEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CurrentDeviceViewActions.updateToLatestAllowedVersion),
      mergeMap(({ deviceId }) =>
        safeMap(
          this.deviceService.scheduleOsUpdate({ udid: deviceId }),
          () => OsUpdatesApiActions.deviceUpdated(),
          (error) => OsUpdatesApiActions.deviceUpdatedError({ error })
        )
      )
    );
  });
}
